/*
 * Step-up (security.md §4.5, protocol.md §4.10). An action whose stamps are stale is refused with
 * `403 stepUpRequired` and a tier; the client proves the tier and retries the original request
 * **once**. Retrying more than once would turn a policy decision into a loop, and retrying without
 * the person's consent would turn a dialog into a click-through. The one exception is an action
 * that needs both tiers — a change of the administrators group, an administrator's creation: the
 * server names the missing password first and the missing code on the retry, so a retry refused for
 * a *higher* tier than was just proved asks once more, for the code alone. It cannot loop: the tier
 * only goes up, and there are two.
 *
 * There are two ways to ask, and which one a surface uses is decided by what the surface is:
 *
 *  - `withStepUp` wraps a call that stands alone — a menu row, a table's action. The prompt is a
 *    single function registered by the one mounted `StepUpHost`, so a document has one step-up
 *    dialog exactly as it has one announcer. Without a host — a page of the sign-in flow, a unit
 *    test — the refusal is simply thrown and the caller shows it.
 *  - `useStepUpField` is for a surface that is *already a form*. It asks in the form itself, as its
 *    last field, and opens no dialog at all: a second window over a half-filled form is where the
 *    typing goes (D-10 D11).
 */

import { createElement, useCallback, useState, type ReactNode } from "react"
import { t } from "../../i18n/index.ts"
import { useModel } from "../../shell/context.tsx"
import type { StepUp } from "../../wire/core.gen.ts"
import { authApi } from "../api.ts"
import { stepUpTierOf, type StepUpTier } from "../errors.ts"
import { StepUpPasswordField } from "./StepUpPasswordField.tsx"

/**
 * How long a proved password stays fresh, mirrored from `internal/core/actor.go`'s
 * `StepUpWindowMs`. The server remains the authority: a client that guesses "fresh" and is refused
 * shows the field and the next attempt carries a password; one that guesses "stale" only asks for a
 * password that was not strictly needed.
 */
const STEP_UP_WINDOW_MS = 15 * 60 * 1000

/** Whether tier 1 holds. A stamp in the future is stale rather than valid, as it is on the server. */
function passwordTierFresh(stepUp: StepUp | undefined, now: number = Date.now()): boolean {
	const at = stepUp?.passwordAt ?? 0
	return at > 0 && at <= now && now - at < STEP_UP_WINDOW_MS
}

/**
 * Resolves true once the tier is proved, false when the person abandoned the dialog. `passwordProved`
 * says the password was proved a moment ago for the same action, so tier 2 asks for the code alone.
 */
export type StepUpPrompt = (tier: StepUpTier, passwordProved: boolean) => Promise<boolean>

let prompt: StepUpPrompt | null = null

/** Registered by the mounted host; `null` unregisters it. */
export function setStepUpPrompt(next: StepUpPrompt | null): void {
	prompt = next
}

/**
 * Runs a call that may need step-up. The call must be repeatable: it is sent again unchanged after
 * the dialog succeeds, and a request that is not idempotent has no business behind a step-up.
 */
export async function withStepUp<T>(call: () => Promise<T>): Promise<T> {
	let proved: StepUpTier | 0 = 0
	for (;;) {
		try {
			return await call()
		} catch (err) {
			const tier = stepUpTierOf(err)
			// A refusal for a tier already proved is the policy's answer, not a question to ask again.
			if (tier === null || prompt === null || tier <= proved) throw err
			const granted = await prompt(tier, proved > 0)
			// Esc abandons the original action; nothing is queued, and the caller sees the refusal.
			if (!granted) throw err
			proved = tier
		}
	}
}

/** What a form gets from `useStepUpField`. */
export interface StepUpFormField {
	/** The session's password tier is stale, so the write ahead needs one typed. */
	needed: boolean
	/** The field to render as the form's last one; `null` while nothing is needed. */
	field: ReactNode
	/**
	 * Whether the form may be sent. `false` marks the field and draws its own error line inside it —
	 * the hook holds the typed password, so it is the only place that knows the box is empty, and a
	 * sentence the field owns is wired to the box (`aria-invalid`, `aria-describedby`) where a
	 * sentence beside it is not.
	 */
	ready: () => boolean
	/** Raises the tier with what was typed, then runs the call. Never opens a dialog. */
	submit: <T>(call: () => Promise<T>) => Promise<T>
}

/**
 * The step-up a form asks for itself (K-08's *New account*). It reads the session's own stamps, so
 * the field appears before the submit rather than after a refusal, and it holds the typed password
 * only for the one call that spends it.
 */
export function useStepUpField(): StepUpFormField {
	const stepUp = useModel((model) => model.me?.stepUp)
	const [password, setPassword] = useState("")
	// The moment this form proved the tier itself. The model's stamp is refreshed by the server's
	// next session read, which is later than the write this form is about to make.
	const [provedAt, setProvedAt] = useState(0)
	// The server refused although the stamps looked fresh — two clocks apart, or a tier let go in
	// another tab. The field appears, and the next attempt carries a password.
	const [refused, setRefused] = useState(false)
	// The form was sent with the box empty. Cleared by the next keystroke, so the line goes as soon as
	// the reader answers it.
	const [missing, setMissing] = useState(false)

	const needed =
		refused || !(passwordTierFresh(stepUp) || passwordTierFresh({ passwordAt: provedAt }))

	const ready = useCallback(() => {
		const empty = needed && password === ""
		setMissing(empty)
		return !empty
	}, [needed, password])

	const submit = useCallback(
		async <T>(call: () => Promise<T>): Promise<T> => {
			if (needed) {
				await authApi.stepUpPassword(password)
				setProvedAt(Date.now())
				setRefused(false)
				setPassword("")
			}
			try {
				return await call()
			} catch (err) {
				if (stepUpTierOf(err) !== null) setRefused(true)
				throw err
			}
		},
		[needed, password],
	)

	// `createElement` and not JSX: this file is the seam another package types against, and its path
	// is part of that contract — a `.tsx` extension would move it.
	return {
		needed,
		field: needed
			? createElement(StepUpPasswordField, {
					value: password,
					onChange: (next: string) => {
						setMissing(false)
						setPassword(next)
					},
					...(missing ? { error: t("auth.stepup.missing") } : {}),
				})
			: null,
		ready,
		submit,
	}
}
